import React, { useEffect, useMemo, useState, useCallback } from "react";
import {
  Modal,
  Typography,
  Form,
  DatePicker,
  TimePicker,
  Input,
  Space,
  Button,
  Alert,
  Tag,
  Spin,
  message,
} from "antd";
import {
  CalendarOutlined,
  FieldTimeOutlined,
  ReloadOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import { useDispatch, useSelector } from "react-redux";
import {
  addAuthorisation,
  editAuthorisationCurrentUser,
  getMyMonthlyAuthorizationProgress,
} from "../../slices/authorisation";
import "./_authorisation.scss";

const { TextArea } = Input;

const ApplyForAuthorisationModal = ({
  title,
  id,
  open,
  handleClose,
  data,
}) => {
  const dispatch = useDispatch();
  const [form] = Form.useForm();

  const [submitting, setSubmitting] = useState(false);
  const [feedback, setFeedback] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);

  const user = useSelector((state) => state.user.fetchMe);
  const { monthlyProgress, status: authorisationStatus } = useSelector(
    (state) => state.authorisation || {}
  );

  const isEdit = useMemo(() => {
    return data && (data.isEdit || data._id);
  }, [data]);

  const totalBalanceHours = 8; // Monthly limit is always 8h

  const isLoading = authorisationStatus === "loading";

  const currentMonthLabel = useMemo(() => {
    if (!selectedDate) {
      return dayjs().format("MMMM YYYY");
    }
    return dayjs(selectedDate).format("MMMM YYYY");
  }, [selectedDate]);

  const usedHours = monthlyProgress?.totalHours || 0;
  const remainingHours =
    typeof monthlyProgress?.remainingHours === "number"
      ? monthlyProgress.remainingHours
      : Math.max(0, totalBalanceHours - usedHours);

  const hasReachedLimit =
    monthlyProgress?.hasReachedLimit || usedHours >= totalBalanceHours;

  // Calculate current auth balance (sum of all authorizations across all time)
  const currentAuthBalance = useMemo(() => {
    if (user?.authorisationBalance !== undefined && user?.authorisationBalanceLeft !== undefined && user?.authorisationBalanceLeft !== null) {
      return user.authorisationBalance - user.authorisationBalanceLeft;
    }
    return 0;
  }, [user?.authorisationBalance, user?.authorisationBalanceLeft]);

  const getMonthlyProgress = useCallback(
    (date) => {
      if (!date) return;
      const month = date.month() + 1;
      const year = date.year();
      dispatch(getMyMonthlyAuthorizationProgress({ month, year }));
    },
    [dispatch]
  );

  // Disable early hours so first selectable hour is 08:00
  const getDisabledTime = useCallback(() => {
    const disabledHours = () =>
      Array.from({ length: 8 }, (_, index) => index); // 0–7 disabled
    return { disabledHours };
  }, []);

  // Initialize form when opening (edit vs create)
  useEffect(() => {
    if (open && data && isEdit) {
      const dateValue = data.date ? dayjs(data.date) : null;
      const startTime =
        data.startTime && dayjs(data.startTime, "HH:mm").isValid()
          ? dayjs(data.startTime, "HH:mm")
          : null;
      const endTime =
        data.endTime && dayjs(data.endTime, "HH:mm").isValid()
          ? dayjs(data.endTime, "HH:mm")
          : null;

      form.setFieldsValue({
        date: dateValue,
        timeRange: startTime && endTime ? [startTime, endTime] : null,
        description: data.description || "",
      });

      if (dateValue && dateValue.isValid()) {
        setSelectedDate(dateValue);
        getMonthlyProgress(dateValue);
      }
    } else if (!open) {
      form.resetFields();
      setFeedback(null);
      setSelectedDate(null);
    }
  }, [open, data, isEdit, form, getMonthlyProgress]);

  const handleModalClose = () => {
    if (submitting) return;
    form.resetFields();
    setFeedback(null);
    setSelectedDate(null);
    handleClose(id);
  };

  const handleReset = () => {
    form.resetFields();
    setFeedback(null);
    setSelectedDate(null);
  };

  const handleDateChange = (value) => {
    setSelectedDate(value || null);
    if (value && value.isValid()) {
      getMonthlyProgress(value);
    }
  };

  const validateTimeRange = (timeRange) => {
    if (!timeRange || timeRange.length !== 2) {
      return "Please select start and end time.";
    }
    const [start, end] = timeRange;
    if (!start || !end) {
      return "Please select start and end time.";
    }
    if (!end.isAfter(start)) {
      return "End time must be after start time.";
    }
    return null;
  };

  const onFinish = async (values) => {
    const { date, timeRange, description } = values;

    const trimmedDescription = (description || "").trim();
    if (!trimmedDescription) {
      message.error("Please provide a description.");
      return;
    }

    if (!date || !dayjs(date).isValid()) {
      message.error("Please select a valid date.");
      return;
    }

    const timeError = validateTimeRange(timeRange);
    if (timeError) {
      message.error(timeError);
      return;
    }

    const [startTimeMoment, endTimeMoment] = timeRange;

    const startTime = startTimeMoment.format("HH:mm");
    const endTime = endTimeMoment.format("HH:mm");

    // Approximate duration in hours (same day)
    const durationMinutes = endTimeMoment.diff(startTimeMoment, "minute");
    const durationHours = durationMinutes > 0 ? durationMinutes / 60 : 0;

    // Optional soft check against remaining monthly hours (do not block edit of past items)
    if (!isEdit && remainingHours <= 0) {
      const msg =
        "You have already reached your monthly authorisation limit (8h). Additional requests may be rejected.";
      setFeedback({ type: "warning", message: msg });
      message.warning(msg);
    } else if (!isEdit && durationHours > remainingHours && remainingHours > 0) {
      const overBy = (durationHours - remainingHours).toFixed(2);
      const msg = `This authorisation exceeds your remaining balance for ${currentMonthLabel} by approximately ${overBy} hour(s). It may still be submitted but can be rejected by HR.`;
      setFeedback({ type: "warning", message: msg });
    }

    try {
      setSubmitting(true);
      setFeedback(null);

      const payload = {
        description: trimmedDescription,
        date: dayjs(date).toISOString(),
        startTime,
        endTime,
      };

      if (isEdit && data?._id) {
        await dispatch(
          editAuthorisationCurrentUser({
            id: data._id,
            Data: payload,
          })
        ).unwrap();
        message.success("Authorisation request updated successfully.");
      } else {
        await dispatch(addAuthorisation(payload)).unwrap();
        message.success("Authorisation request submitted successfully.");
      }

      handleModalClose();
    } catch (error) {
      const errorMessage =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        error?.message ||
        (typeof error === "string"
          ? error
          : "Failed to submit the authorisation request. Please try again.");

      setFeedback({
        type: "error",
        message: errorMessage,
      });
      message.error(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  const headerTitle = isEdit
    ? "Edit Authorisation Request"
    : title || "Apply for authorisation";

  const headerSubtitle = isEdit
    ? "Update your authorisation request details and review your monthly balance."
    : "Submit a new authorisation request and keep track of your monthly balance.";

  return (
    <Modal
      open={open}
      onCancel={handleModalClose}
      footer={null}
      destroyOnClose
      width={720}
      centered
      className="apply-for-leave-modal apply-for-authorisation-modal"
    >
      <div className="apply-for-leave-modal__header">
        <Typography.Title level={3} style={{ marginBottom: 4 }}>
          {headerTitle}
        </Typography.Title>
        <Typography.Paragraph type="secondary" style={{ marginBottom: 16 }}>
          {headerSubtitle}
        </Typography.Paragraph>
      </div>

      {/* Balance overview */}
      <div style={{ marginBottom: 16 }}>
        <Space direction="vertical" size={4}>
          <Typography.Text strong>Authorisation balance ({currentMonthLabel})</Typography.Text>
          <Space wrap size={[8, 8]}>
            <Tag color="blue">
              Current Auth Balance: {currentAuthBalance.toFixed(2)}h
            </Tag>
            {monthlyProgress?.authorizations != null && (
              <Tag>
                {monthlyProgress.authorizations} request
                {monthlyProgress.authorizations === 1 ? "" : "s"} this month
              </Tag>
            )}
          </Space>
          {!selectedDate && !isEdit && (
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              Select a date to view the exact monthly balance for that period.
            </Typography.Text>
          )}
        </Space>
      </div>

      {feedback && (
        <Alert
          type={feedback.type}
          message={feedback.message}
          showIcon
          closable
          style={{ marginBottom: 16 }}
          onClose={() => setFeedback(null)}
        />
      )}

      <Spin spinning={isLoading && !isEdit}>
        <Form
          form={form}
          layout="vertical"
          onFinish={onFinish}
          requiredMark={false}
        >
          <Form.Item
            label="Date"
            name="date"
            rules={[
              {
                required: true,
                message: "Please select the authorisation date.",
              },
            ]}
          >
            <DatePicker
              className="authorisation-input"
              style={{ width: "100%" }}
              format="DD MMM YYYY"
              suffixIcon={<CalendarOutlined />}
              onChange={handleDateChange}
            />
          </Form.Item>

          <Form.Item
            label="Time"
            name="timeRange"
            rules={[
              {
                validator: (_, value) => {
                  const error = validateTimeRange(value);
                  if (error) {
                    return Promise.reject(new Error(error));
                  }
                  return Promise.resolve();
                },
              },
            ]}
          >
            <TimePicker.RangePicker
              className="authorisation-input"
              style={{ width: "100%" }}
              format="HH:mm"
              // Keep standard 00, 15, 30, 45 minute options
              minuteStep={15}
              suffixIcon={<FieldTimeOutlined />}
              disabledTime={getDisabledTime}
              hideDisabledOptions
            />
          </Form.Item>

          <Form.Item
            label="Reason / Description"
            name="description"
            style={{ marginBottom: 24 }}
            rules={[
              {
                validator: (_, value) => {
                  const trimmed = (value || "").trim();
                  if (!trimmed) {
                    return Promise.reject(
                      new Error("Please provide a description.")
                    );
                  }
                  if (trimmed.length < 10) {
                    return Promise.reject(
                      new Error(
                        "Description must be at least 10 characters long."
                      )
                    );
                  }
                  return Promise.resolve();
                },
              },
            ]}
          >
            <TextArea
              rows={4}
              placeholder="Provide details about your authorisation request..."
              showCount
              maxLength={500}
              style={{ resize: "none" }}
            />
          </Form.Item>

          <Form.Item style={{ marginBottom: 0 }}>
            <Space style={{ width: "100%", justifyContent: "flex-end" }}>
              <Button
                icon={<ReloadOutlined />}
                onClick={handleReset}
                disabled={submitting}
              >
                Reset
              </Button>
              <Button
                type="primary"
                htmlType="submit"
                loading={submitting}
              >
                {isEdit ? "Update Authorisation" : "Apply for Authorisation"}
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Spin>
    </Modal>
  );
};

export default ApplyForAuthorisationModal;


